import { Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { ModalController, ToastController, Platform } from '@ionic/angular';
import { AppUpdateComponent } from '../components/app-update/app-update.component';

/**
 * Håndterer app-oppdateringer (Angular Service Worker):
 *  - Abonnerer på nye versjoner og viser en pen modal.
 *  - Sjekker periodisk (hver 30. min) og ved app-resume, slik at en ny deploy
 *    oppdages selv om appen står åpen lenge (tidligere manglet dette).
 *  - Manuell sjekk-knapp (fra innstillinger) via checkNow().
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private initialized = false;
  private modalOpen = false;
  private readonly CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 min

  constructor(
    private swUpdate: SwUpdate,
    private modalCtrl: ModalController,
    private toastCtrl: ToastController,
    private platform: Platform
  ) {}

  /** Kalles én gang ved app-oppstart. */
  init(): void {
    if (this.initialized || !this.swUpdate.isEnabled) {
      return;
    }
    this.initialized = true;

    this.swUpdate.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        this.openModal();
      }
    });

    // Periodisk sjekk + ved resume, så lange økter også fanger opp ny versjon
    setInterval(() => this.silentCheck(), this.CHECK_INTERVAL_MS);
    this.platform.resume.subscribe(() => this.silentCheck());
  }

  private silentCheck(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.checkForUpdate().catch(() => { /* ignorer */ });
    }
  }

  /** Manuell sjekk fra innstillinger. Viser modal ved funn, ellers en toast. */
  async checkNow(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      await this.toast('Oppdateringssjekk er ikke tilgjengelig i denne visningen.');
      return;
    }
    let found = false;
    try {
      found = await this.swUpdate.checkForUpdate();
    } catch {
      found = false;
    }
    if (found) {
      this.openModal();
    } else {
      await this.toast('Du har allerede den nyeste versjonen.');
    }
  }

  private async openModal(): Promise<void> {
    if (this.modalOpen) {
      return;
    }
    this.modalOpen = true;
    const modal = await this.modalCtrl.create({
      component: AppUpdateComponent,
      cssClass: 'app-update-modal',
      backdropDismiss: false,
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    this.modalOpen = false;

    if (data === 'install') {
      try {
        await this.swUpdate.activateUpdate();
      } catch {
        /* ignorer — reload henter uansett ny versjon */
      }
      window.location.reload();
    }
  }

  private async toast(message: string): Promise<void> {
    const t = await this.toastCtrl.create({
      message,
      duration: 2500,
      position: 'bottom',
    });
    await t.present();
  }
}
